// hooks/projectCode/useProjectCode.js
import { useCallback, useMemo } from 'react';
import { useProjectCodeContext } from './context';
import { ACTIONS } from './reducer';
import { apiCaller, APIError, createSearchCriteria, createPageable } from 'utils/axios';
import { API_ENDPOINTS } from 'hooks/api/apiEndpoints';
import Swal from 'sweetalert2';

export const useProjectCode = () => {
    const [state, dispatch] = useProjectCodeContext();

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const handleError = useCallback(
        (error) => {
            let errorMessage = 'An unexpected error occurred';
            let errors = null;

            if (error instanceof APIError) {
                errorMessage = error.message;
                errors = error.errors;
            }

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });

            dispatch({ type: ACTIONS.SET_ERROR, payload: { message: errorMessage, errors } });
        },
        [dispatch]
    );

    const setFilter = useCallback(
        (filterData) => {
            dispatch({ type: ACTIONS.SET_FILTER, payload: filterData });
        },
        [dispatch]
    );

    const setSort = useCallback(
        (sortData) => {
            dispatch({ type: ACTIONS.SET_SORT, payload: sortData });
        },
        [dispatch]
    );

    const setPage = useCallback(
        (pageData) => {
            dispatch({ type: ACTIONS.SET_PAGE, payload: pageData });
        },
        [dispatch]
    );

    const fetchProjectCodes = useCallback(async () => {
        try {
            setLoading(true);
            const { filter, pagination, sort } = state;

            const searchCriteria = Object.entries(filter)
                .filter(([_, value]) => value !== null && value !== undefined)
                .map(([key, value]) =>
                    createSearchCriteria({
                        key,
                        value,
                        operation: 'EQUAL'
                    })
                );

            const pageable = createPageable({
                page: pagination.page,
                size: pagination.size,
                sort: [{ field: sort.field, direction: sort.direction }]
            });

            const data = await apiCaller.search(API_ENDPOINTS.PROJECT_CODES, searchCriteria, pageable);
            dispatch({ type: ACTIONS.SET_PROJECT_CODES, payload: data });
            return data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, dispatch, state]);

    const searchProjectCodes = useCallback(
        async (searchParams = {}, pageParams = {}) => {
            setFilter(searchParams);
            setPage(pageParams);
            return fetchProjectCodes();
        },
        [setFilter, setPage, fetchProjectCodes]
    );

    const createProjectCode = useCallback(
        async (projectCodeData) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(API_ENDPOINTS.PROJECT_CODES, projectCodeData);
                dispatch({ type: ACTIONS.ADD_PROJECT_CODE, payload: data });

                showSuccessMessage('Project code created successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateProjectCode = useCallback(
        async (id, projectCodeData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.PROJECT_CODES}/${id}`, projectCodeData);
                dispatch({ type: ACTIONS.UPDATE_PROJECT_CODE, payload: { id, data } });

                showSuccessMessage('Project code updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const deleteProjectCode = useCallback(
        async (id) => {
            try {
                setLoading(true);
                await apiCaller.delete(`${API_ENDPOINTS.PROJECT_CODES}/${id}`);
                dispatch({ type: ACTIONS.DELETE_PROJECT_CODE, payload: id });

                showSuccessMessage('Project code deleted successfully');
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const getProjectCodeById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.PROJECT_CODES}/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_PROJECT_CODE, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, [dispatch]);

    const listProjectCodes = useCallback(
        async (page = 0, size = 10) => {
            try {
                setLoading(true);
                const data = await apiCaller.list(API_ENDPOINTS.PROJECT_CODES, page, size);
                dispatch({ type: ACTIONS.SET_PROJECT_CODES, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    return useMemo(
        () => ({
            // State
            projectCodes: state.projectCodes,
            selectedProjectCode: state.selectedProjectCode,
            loading: state.loading,
            error: state.error,
            filter: state.filter,
            pagination: state.pagination,
            sort: state.sort,
            lastUpdated: state.lastUpdated,
            listProjectCodes,

            // List Operations
            setFilter,
            setSort,
            setPage,
            fetchProjectCodes,
            searchProjectCodes,

            // CRUD Operations
            createProjectCode,
            updateProjectCode,
            deleteProjectCode,
            getProjectCodeById,

            // Utilities
            clearError
        }),
        [
            state,
            setFilter,
            setSort,
            setPage,
            fetchProjectCodes,
            searchProjectCodes,
            createProjectCode,
            updateProjectCode,
            deleteProjectCode,
            getProjectCodeById,
            clearError,
            listProjectCodes
        ]
    );
};

export default useProjectCode;
